/**
 * StepConnect — Dynamic connection step based on manifest.auth_method.
 *
 * - oauth: OAuth popup flow (reuses existing useOAuthInitiate/useOAuthCallback)
 * - app_install: GitHub installation selector (reuses useEnrollmentStatus)
 * - credentials: Dynamic form from manifest.credential_fields
 * - none: Auto-advance (no auth needed)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useI18n } from '@/i18n';
import { Button, Skeleton } from '@/components/ui';
import { CheckCircle, ExternalLink, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useOAuthInitiate, useOAuthCallback } from '../../hooks/use-datasources';
import { useEnrollmentStatus } from '../../hooks/use-oauth-apps';
import type { EnrichedProviderManifest, OAuthProvider, CredentialField } from '../../types';

interface StepConnectProps {
  manifest: EnrichedProviderManifest;
  credentialToken: string | null;
  onCredentialToken: (token: string) => void;
  credentials: Record<string, string>;
  onCredentials: (creds: Record<string, string>) => void;
  installationId: number | null;
  onInstallationId: (id: number) => void;
}

export function StepConnect({
  manifest,
  credentialToken,
  onCredentialToken,
  credentials,
  onCredentials,
  installationId,
  onInstallationId,
}: StepConnectProps) {
  const { t } = useI18n();

  switch (manifest.auth_method) {
    case 'oauth':
      return (
        <OAuthConnect
          manifest={manifest}
          credentialToken={credentialToken}
          onConnected={onCredentialToken}
        />
      );
    case 'app_install':
      return (
        <AppInstallConnect
          manifest={manifest}
          installationId={installationId}
          onSelect={onInstallationId}
        />
      );
    case 'credentials':
      return (
        <CredentialsConnect
          manifest={manifest}
          credentials={credentials}
          onChange={onCredentials}
        />
      );
    case 'none':
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
          <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            {t('datasources.wizard.noAuthRequired')}
          </p>
          <p className="text-sm mt-1">{t('datasources.wizard.noAuthHint')}</p>
        </div>
      );
    default:
      return null;
  }
}

// =============================================================================
// OAuth Connect
// =============================================================================

function OAuthConnect({
  manifest,
  credentialToken,
  onConnected,
}: {
  manifest: EnrichedProviderManifest;
  credentialToken: string | null;
  onConnected: (token: string) => void;
}) {
  const { t } = useI18n();
  const initiate = useOAuthInitiate();
  const callback = useOAuthCallback();
  const [connecting, setConnecting] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const stateRef = useRef<string>('');

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.data?.type !== 'oauth-callback') return;
      const { code, state, error } = event.data;

      if (error) {
        toast.error(t('datasources.oauth.consentDenied'));
        setConnecting(false);
        return;
      }
      if (state !== stateRef.current) {
        toast.error(t('datasources.oauth.stateMismatch'));
        setConnecting(false);
        return;
      }

      try {
        const result = await callback.mutateAsync({ code, state });
        onConnected(result.credential_token);
        toast.success(t('datasources.oauth.connected'));
      } catch {
        toast.error(t('datasources.oauth.callbackFailed'));
      } finally {
        setConnecting(false);
      }
    },
    [callback, onConnected, t],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleConnect = async () => {
    if (credentialToken) return;
    setConnecting(true);

    try {
      const { authorization_url, state } = await initiate.mutateAsync({
        provider: manifest.oauth_provider as OAuthProvider,
        // ISS-385: let the backend pick per-datasource scopes (e.g. OneDrive
        // must not request Sites.Read.All, which personal accounts reject).
        datasourceProvider: manifest.provider_type,
      });
      stateRef.current = state;

      const w = 600;
      const h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      popupRef.current = window.open(
        authorization_url,
        'oauth-popup',
        `width=${w},height=${h},left=${left},top=${top},popup=yes`,
      );

      if (!popupRef.current) {
        toast.error(t('datasources.oauth.initFailed'));
        setConnecting(false);
      }
    } catch {
      toast.error(t('datasources.oauth.initFailed'));
      setConnecting(false);
    }
  };

  if (credentialToken) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-6 py-4">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-green-700 dark:text-green-300">
            {t('datasources.oauth.accountConnected')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 max-w-sm mx-auto">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {t('datasources.wizard.connectAccount')}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
        {t('datasources.wizard.connectAccountHint')}
      </p>
      <Button
        variant="outline"
        onClick={handleConnect}
        disabled={connecting}
        className="w-full justify-center gap-2 py-6 text-base"
      >
        {connecting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <ExternalLink className="w-5 h-5" />
        )}
        {connecting
          ? t('datasources.oauth.connecting')
          : t('datasources.oauth.connectWith', { provider: manifest.display_name })}
      </Button>
    </div>
  );
}

// =============================================================================
// App Installation Connect
// =============================================================================

function AppInstallConnect({
  installationId,
  onSelect,
}: {
  manifest: EnrichedProviderManifest;
  installationId: number | null;
  onSelect: (id: number) => void;
}) {
  const { t } = useI18n();
  const { data: enrollment, isLoading } = useEnrollmentStatus();

  if (isLoading) {
    return (
      <div className="space-y-3 max-w-md mx-auto py-8">
        <Skeleton className="h-12 rounded-lg" />
        <Skeleton className="h-12 rounded-lg" />
      </div>
    );
  }

  const installations = enrollment?.github_installations ?? [];

  if (installations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {t('datasources.wizard.noInstallations')}
        </p>
        <p className="text-sm">{t('datasources.wizard.noInstallationsHint')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8 space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        {t('datasources.wizard.selectInstallation')}
      </h3>
      <div className="space-y-2">
        {installations.map((inst) => (
          <button
            key={inst.id}
            type="button"
            onClick={() => onSelect(inst.installation_id)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
              installationId === inst.installation_id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
            }`}
          >
            <span className="font-medium text-gray-900 dark:text-white">
              {inst.account_login}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {inst.account_type}
            </span>
            {installationId === inst.installation_id && (
              <CheckCircle className="w-4 h-4 text-blue-500" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Credentials Form Connect
// =============================================================================

function CredentialsConnect({
  manifest,
  credentials,
  onChange,
}: {
  manifest: EnrichedProviderManifest;
  credentials: Record<string, string>;
  onChange: (creds: Record<string, string>) => void;
}) {
  const { t } = useI18n();

  const handleChange = (key: string, value: string) => {
    onChange({ ...credentials, [key]: value });
  };

  const isFieldVisible = (field: CredentialField): boolean => {
    if (!field.depends_on) return true;
    return Object.entries(field.depends_on).every(
      ([depKey, depValue]) => credentials[depKey] === depValue,
    );
  };

  return (
    <div className="max-w-md mx-auto py-8 space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {t('datasources.wizard.enterCredentials')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('datasources.wizard.enterCredentialsHint')}
        </p>
      </div>

      {manifest.credential_fields.map((field: CredentialField) => {
        if (!isFieldVisible(field)) return null;

        return (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t(field.label)}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.type === 'select' && field.options ? (
              <select
                value={credentials[field.key] ?? ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{field.placeholder ?? 'Select...'}</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.label)}
                  </option>
                ))}
              </select>
            ) : field.type === 'textarea' ? (
              <textarea
                value={credentials[field.key] ?? ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                placeholder={field.placeholder ?? ''}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
              />
            ) : (
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={field.type === 'password' ? 'password' : 'text'}
                  value={credentials[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder ?? ''}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
